import { formatAmount } from '../../utils/orderDisplay'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { DataTable } from '@/components/ui/data-table'
import { StatusBadge } from '../products/PageHelpers'
import { useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { getRefunds } from '../../services/refund'
import { formatTimestamp } from '../../utils/format'
import type { MerchantRefund, Pagination } from '../../types'

const statusLabels: Record<string, string> = {
  CREATED: '已建立',
  SUBMITTING: '提交中',
  PROCESSING: '處理中',
  SUCCEEDED: '已成功',
  FAILED: '已失敗',
  UNKNOWN: '未知',
}

const statusColors: Record<string, string> = {
  CREATED: 'default',
  SUBMITTING: 'blue',
  PROCESSING: 'gold',
  SUCCEEDED: 'green',
  FAILED: 'red',
  UNKNOWN: 'default',
}

export default function Refunds() {
  const navigate = useNavigate()
  const [refunds, setRefunds] = useState<MerchantRefund[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 20,
    total: 0,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)

  const fetchRefunds = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getRefunds({ page, size })
      .then((res) => {
        setRefunds(res.data.items)
        setPagination(res.data.pagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchRefunds()
  }, [])

  const columns = [
    {
      key: 'id',
      header: '退款號',
      render: (row: MerchantRefund) => row['id'] ?? '-',
    },
    {
      key: 'orderId',
      header: '訂單號',
      render: (row: MerchantRefund) => {
        const orderId = row['orderId']
        return (
          <Button
            onClick={() => navigate(`/dashboard/orders/${orderId}`)}
            variant={'link'}
            type="button"
            size={'sm'}
          >
            {orderId}
          </Button>
        )
      },
    },
    {
      key: 'amount',
      header: '金額',
      className: 'text-right tabular-nums',
      render: (row: MerchantRefund) => {
        const record = row
        return `${formatAmount(record.amount)} ${record.currency}`
      },
    },
    {
      key: 'status',
      header: '狀態',
      render: (row: MerchantRefund) => {
        const status = row['status']
        return (
          <StatusBadge color={statusColors[status]}>
            {statusLabels[status] || status}
          </StatusBadge>
        )
      },
    },
    {
      key: 'reason',
      header: '原因',
      render: (row: MerchantRefund) => {
        const reason = row['reason']
        return reason || '-'
      },
    },
    {
      key: 'errorMessage',
      header: '錯誤信息',
      render: (row: MerchantRefund) => {
        const message = row['errorMessage']
        return message ? (
          <span className="text-destructive">{message}</span>
        ) : (
          '-'
        )
      },
    },
    {
      key: 'createdAt',
      header: '創建時間',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantRefund) => {
        const ts = row['createdAt']
        return formatTimestamp(ts)
      },
    },
    {
      key: 'succeededAt',
      header: '成功時間',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantRefund) => {
        const ts = row['succeededAt']
        return formatTimestamp(ts)
      },
    },
  ]

  return (
    <div>
      <h2
        style={{ marginBottom: 16 }}
        className="mb-4 text-xl font-semibold tracking-tight"
      >
        退款記錄
      </h2>
      <Card>
        <div className="space-y-4 px-5">
          <DataTable
            rowKey={'id'}
            loading={loading}
            columns={columns}
            data={refunds}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total,
              onChange: (page, size) => {
                if (size !== pageSize) {
                  setPageSize(size)
                  fetchRefunds(1, size)
                } else {
                  fetchRefunds(page, size)
                }
              },
            }}
          />
        </div>
      </Card>
    </div>
  )
}
